import type { BuyerFeesAnswer } from "@hakili/contracts";
import { isPaymentMean } from "@hakili/domain-types";
import { MALI_PREFIX, PhoneInvalid, formatPhone, normalisePhone, t } from "@hakili/i18n";
import { useEffect, useState } from "react";

import {
  Paiement,
  type PaymentDestination,
  type MeanView,
  type PaymentStep,
  type ServedMandate,
} from "./paiement.js";

/**
 * `round C3-A`, `C3-A.2`: **the payment screen's own reading.**
 *
 * Named in English , `no-french-identifier` refused `PaiementEcran` and it was
 * right: `09-01` is the founder's decision of 5 September, and a compound built
 * on a French stem reads as French however the other half is spelled. The pure
 * component beside it keeps `Paiement`, which the frozen list already carries.
 *
 * `Paiement` is a pure rendering of three door answers , it fetches nothing and
 * decides nothing , which is what lets a unit test stand it up with figures and
 * what keeps `9.2.5`'s purity rule true one layer up. This file is the layer
 * that asks, and it is separate for the reason `04` §4.6.4 separates a screen
 * from its shell: a component that fetches is a component that cannot be
 * rendered twice with two states.
 *
 * **It asks once, on the file's id.** A re-read on every render is round 185's
 * defect , seven and a half requests a second on an idle screen in front of a
 * founder , and the dependency is the id and the two stable callbacks, never
 * the object that holds them.
 */
export interface PaymentScreenProps {
  readonly dossierId: string;
  readonly vehicleLabel: string;
  readonly sellerName: string;
  /** The chargé the birth chose (`P1-DOS-010`), or `null` for the exception. */
  readonly ownerName: string | null;
  /** The FILE's own reference , what a buyer quotes on his transfer. */
  readonly dossierReference: string;
  readonly load: (dossierId: string) => Promise<{
    readonly fees: BuyerFeesAnswer;
    readonly mandate: unknown;
    readonly extraInterestAmount: number | null;
    /** `C3-B.0`: the one estimate, the fee inside, for the mandate's line. */
    readonly estimateAmount: number | null;
  }>;
  readonly accept: (
    dossierId: string,
    input: { readonly feeFinancingRequested: boolean; readonly mandateVersion: number },
  ) => Promise<void>;
  /** `WF-CLI-10` §7: the buyer asks to pay, by the means he chose. */
  readonly ask: (
    dossierId: string,
    input: { readonly method: string; readonly msisdn?: string },
  ) => Promise<{ readonly reference: string; readonly paymentRequestId: string }>;
  /** §8's own question, asked again: has it landed? */
  readonly poll: (paymentId: string) => Promise<{ readonly status: string }>;
  /** #493 addendum 1: cancels the active declaration , the way back to the choice of means. */
  readonly cancelDeclaration: (dossierId: string) => Promise<void>;
  /** #493 addendum 2: « J'ai effectué le paiement », with the receipt's reference when he has one. */
  /** Answers the door's own date of the declaration. */
  readonly declarePaid: (dossierId: string, reference: string | null) => Promise<string>;
  /** §9: the buyer's own door out of `intake`. */
  readonly launch: (dossierId: string) => Promise<void>;
  readonly onBack: () => void;
  readonly onLaunched: () => void;
  /**
   * `PAY-M.5`: the buyer's own number, E.164 from `/me`, pre-filling the push
   * field the way the drawing shows it , the indicatif apart, the eight digits
   * in pairs. `null` when the session carries none; the field is then empty.
   */
  readonly buyerPhone: string | null;
}

/**
 * The fee is paid once its client share is settled. `clientStatus` is on the minted fee only: a QUOTE has no settlement
 * state, which is what `quoted` says (`GENERIC-DOOR-SWEEP`, Arc 2: the answer is `@hakili/contracts`' {@link BuyerFeesAnswer}).
 */
const isPaid = (fee: BuyerFeesAnswer["fee"]): boolean => "clientStatus" in fee && fee.clientStatus === "paye";

/** The means the screen knows by code; a code outside the platform's five is not drawn (the pin is the integration spec's). */
const knownMeans = (means: BuyerFeesAnswer["means"]): readonly MeanView[] =>
  means.flatMap((one) => (isPaymentMean(one.code) ? [{ ...one, code: one.code }] : []));

/** `+22376123456` → `76 12 34 56`; a foreign or absent number → as it is, or empty. */
const localPairs = (e164: string | null): string => {
  if (e164 === null || e164 === "") return "";
  const shown = formatPhone(e164);
  return shown.startsWith("+223 ") ? shown.slice("+223 ".length) : shown;
};

/**
 * The payment number's prefix, for THIS field only (registration is untouched):
 * `+223` for a Malian session number or none, empty for a foreign one, whose
 * whole number stays in the field with its own prefix.
 */
const prefixOf = (e164: string | null): string =>
  e164 === null || e164 === "" || e164.startsWith(MALI_PREFIX) ? MALI_PREFIX : "";

/**
 * The prefix and the number as the buyer left them → E.164 for the door. A
 * number that carries its own `+` or `00` stands alone; otherwise the prefix is
 * put in front, and **an empty prefix is refused rather than assumed Malian**.
 */
const composeMsisdn = (prefix: string, number: string): string => {
  const strip = (raw: string): string => raw.replaceAll(/[\s.\-()]/gu, "");
  const own = strip(number);
  if (own.startsWith("+") || own.startsWith("00")) return normalisePhone(own).e164;
  let lead = strip(prefix);
  if (lead.startsWith("00")) lead = `+${lead.slice(2)}`;
  if (lead === "" || lead === "+") throw new PhoneInvalid(t("client.paiement.indicatifRequis"));
  if (!lead.startsWith("+")) lead = `+${lead}`;
  return normalisePhone(`${lead}${own}`).e164;
};

/**
 * The chosen method's OWN destination. **No fallback to another method's details**: the legacy
 * `hakiliAccount` is the bank account and is never the destination of a mobile means.
 */
const destinationOf = (
  mean: MeanView["code"],
  destinations: BuyerFeesAnswer["destinations"],
): PaymentDestination | null => {
  if (mean === "virement") {
    return destinations.virement === null ? null : { kind: "bank", ...destinations.virement };
  }
  if (mean === "orange" || mean === "moov") {
    const own = destinations[mean];
    return own === null ? null : { kind: "mobile", number: own.number };
  }
  return null;
};

export function PaymentScreen(props: PaymentScreenProps): React.JSX.Element | null {
  const [fees, setFees] = useState<BuyerFeesAnswer | null>(null);
  const [mandate, setMandate] = useState<ServedMandate | null>(null);
  const [extra, setExtra] = useState<number | null>(null);
  const [estimateAmount, setEstimateAmount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [step, setStep] = useState<PaymentStep>("choix");
  const [mean, setMean] = useState<MeanView["code"]>("orange");
  const [msisdn, setMsisdn] = useState(localPairs(props.buyerPhone));
  const [prefix, setPrefix] = useState(prefixOf(props.buyerPhone));
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [declared, setDeclared] = useState<{ declaredAt: string; reference: string | null } | null>(null);
  const [receiptReference, setReceiptReference] = useState("");

  const { dossierId, load } = props;
  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const answer = await load(dossierId);
        if (!run.live) return;
        const read = answer.fees;
        setFees(read);
        /**
         * `round C3-A`, `C3-A.4`: **a buyer who comes back after paying does
         * not meet the choice screen again.**
         *
         * Found while shooting the pair. The step is this screen's own state,
         * so it began at `choix` on every mount , and a man who paid by
         * transfer on Tuesday, closed the app and opened it on Thursday when
         * Hakili confirmed was shown the three means and a control asking him
         * to pay again. Nothing would have taken his money twice , the door
         * refuses that , but he could not tell from the screen that his money
         * had arrived, which is the one thing he opened it to learn.
         *
         * The DOOR already knows: `clientStatus` is `paye` once the money is
         * in. The step is derived from it rather than remembered, which is also
         * why `attente` is not derived here , a pending request's id is not on
         * this answer, and a screen that claimed to be waiting without one
         * could not poll.
         */
        if (isPaid(read.fee)) setStep("confirme");
        /**
         * #493 addenda: **a declaration that stands is the screen's state , the step is derived
         * from the door's answer, not remembered.** A buyer who comes back finds the details
         * (or his declaration) and not the payment form; the answer now carries the request's
         * id, so the screen can poll.
         */
        const standing = read.declaration;
        if (!isPaid(read.fee) && standing !== null) {
          if (isPaymentMean(standing.method)) setMean(standing.method);
          setPaymentId(standing.paymentRequestId);
          setDeclared(
            standing.declaredAt === null
              ? null
              : { declaredAt: standing.declaredAt, reference: standing.clientReference },
          );
          setStep("attente");
        }
        setMandate((answer.mandate as { mandate?: ServedMandate } | null)?.mandate ?? null);
        setExtra(answer.extraInterestAmount);
        setEstimateAmount(answer.estimateAmount);
      } catch (failure) {
        // The api's OWN sentence (`4.5.5`), never a sentence of this screen's
        // about somebody's money. A gate may close, never in silence.
        if (run.live) {
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
        }
      }
    })();
    return () => {
      run.live = false;
    };
  }, [dossierId, load]);

  /**
   * `WF-CLI-10` §8: **the confirmation arrives by the PROVIDER's callback, and
   * this screen asks the door again.**
   *
   * Polling and not a promise of a socket: the money lands when the provider
   * says so, and a screen that claimed to know sooner would be inventing. It
   * runs only while a PUSH is pending , a transfer is confirmed by a person on
   * a working day, and asking every two seconds for a day would be a question
   * nobody is answering.
   */
  useEffect(() => {
    if (step !== "attente" || paymentId === null || mean === "virement") return undefined;
    const run = { live: true };
    const timer = setInterval(() => {
      void (async () => {
        try {
          const answer = await props.poll(paymentId);
          if (!run.live) return;
          if (answer.status === "confirme") setStep("confirme");
        } catch {
          // A failed poll is not a failed payment. The next tick asks again,
          // and the buyer is told nothing he would have to act on.
        }
      })();
    }, 2_000);
    return () => {
      run.live = false;
      clearInterval(timer);
    };
    // `props.poll` is a `useCallback` over the api, stable for the session's
    // life , round 185's lesson about depending on the object that holds it.
  }, [step, paymentId, mean, props.poll]);

  const cancelAndBack = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    try {
      await props.cancelDeclaration(dossierId);
      setDeclared(null);
      setPaymentId(null);
      setReceiptReference("");
      setStep("choix");
    } catch (failure) {
      setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
    } finally {
      setBusy(false);
    }
  };

  if (fees === null) return null;

  const intention = fees.intention === "financement" ? "financement" : "comptant";
  return (
    <Paiement
      fee={{
        // `PAY-P`: NO figure while no price is published , a dash, never a zero (`R81`).
        clientAmount: fees.fee.clientAmount,
        quoted: fees.fee.quoted,
        ...("unpublished" in fees.fee ? { unpublished: fees.fee.unpublished } : {}),
      }}
      intention={intention}
      vehicleLabel={props.vehicleLabel}
      // `C3-A.2`: the DOOR's, denormalised at creation by `04` §4.2.4's own
      // rule and stored since round 192 , the projection simply never returned
      // it, so `WF-CLI-10` §1 had a dash where a name was already there.
      sellerName={props.sellerName}
      // `P1-DOS-010` (`round CN-A`, `CN-A.2`): `I17` holds , the chargé is
      // chosen in the transaction that bears the file , and the door answers
      // the name. A dash stays for the one file born while nobody was
      // available, which is the exception « Arrivées » shows.
      ownerName={props.ownerName}
      fileFees={fees.fileFees}
      extraInterestAmount={extra}
      estimateAmount={estimateAmount}
      mandate={mandate}
      busy={busy}
      refusal={refusal}
      onBack={props.onBack}
      onAcceptMandate={(input) => {
        void (async () => {
          setBusy(true);
          setRefusal(null);
          try {
            await props.accept(dossierId, input);
          } catch (failure) {
            setRefusal(
              (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
            );
          } finally {
            setBusy(false);
          }
        })();
      }}
      means={knownMeans(fees.means)}
      step={step}
      mean={mean}
      msisdn={msisdn}
      prefix={prefix}
      onMean={(code) => {
        setMean(code);
        setRefusal(null);
      }}
      onMsisdn={setMsisdn}
      onPrefix={setPrefix}
      referenceToQuote={props.dossierReference}
      destination={destinationOf(mean, fees.destinations)}
      transferConfirmationHours={fees.transferConfirmationHours}
      declared={declared}
      receiptReference={receiptReference}
      onReceiptReference={setReceiptReference}
      onDeclarePaid={(reference) => {
        void (async () => {
          setBusy(true);
          setRefusal(null);
          try {
            const declaredAt = await props.declarePaid(dossierId, reference);
            setDeclared({ declaredAt, reference });
          } catch (failure) {
            setRefusal(
              (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
            );
          } finally {
            setBusy(false);
          }
        })();
      }}
      onCancelDeclaration={() => {
        void cancelAndBack();
      }}
      onChangeMean={() => {
        // « Changer de moyen de paiement »: since a dossier carries one declaration at a time, the
        // way back is to cancel the active one (audited), then choose again. Never silent.
        void cancelAndBack();
      }}
      onLaunch={() => {
        void (async () => {
          setBusy(true);
          setRefusal(null);
          try {
            await props.launch(dossierId);
            props.onLaunched();
          } catch (failure) {
            setRefusal(
              (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
            );
          } finally {
            setBusy(false);
          }
        })();
      }}
      onContinue={() => {
        /**
         * `WF-CLI-10` §7: **he asks, by the means he chose.**
         *
         * The reference comes back from the door and is what the transfer step
         * prints for him to quote , the same string the staff confirmation
         * matches a statement line on. A screen that invented one would send a
         * buyer to his bank with a number nobody could find.
         */
        void (async () => {
          setBusy(true);
          setRefusal(null);
          try {
            // The door takes E.164 and reads the number AGAIN on every send
            // (`C3-A`); the field holds the eight digits the drawing shows.
            const asked = await props.ask(dossierId, {
              method: mean,
              ...(mean === "virement" ? {} : { msisdn: composeMsisdn(prefix, msisdn) }),
            });
            // The request's own reference is the KEY and stays here: the
            // screen quotes the FILE's reference, and a key on a transfer slip
            // is thirty-six characters somebody will get wrong.
            setPaymentId(asked.paymentRequestId);
            setStep("attente");
          } catch (failure) {
            // The door's OWN sentence , `4.5.5`. A provider that refuses says
            // why, and `P1-PAY-022`'s branch is asserted at the unit level
            // because the stub invents no failure (`A48`).
            setRefusal(
              (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
            );
          } finally {
            setBusy(false);
          }
        })();
      }}
    />
  );
}
